import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  adminCertificateHistory,
  adminDownloadCertificate,
  adminResendCertificate,
  saveBlob,
  type ConferenceCertificateItem,
} from "../api";
import { formatDateTime } from "../format";
import { PageHeader } from "../shells/PageHeader";

export function AdminCertificatesHistoryPage() {
  const { t, locale } = useLanguage();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [language, setLanguage] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rows, setRows] = useState<ConferenceCertificateItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await adminCertificateHistory({
        q: q.trim() || undefined,
        status: status || undefined,
        language: language || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      setRows(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFilter = (e: FormEvent) => {
    e.preventDefault();
    load().catch(() => undefined);
  };

  const onDownload = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      const { blob, filename } = await adminDownloadCertificate(id);
      saveBlob(blob, filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const onResend = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await adminResendCertificate(id);
      setMessage(t.admin.certResendDone);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t.admin.certHistory}
        lead={t.admin.certHistoryLead}
        backTo="/admin/certificates"
        actions={
          <Link to="/admin/certificates" className="dn-btn dn-btn-ghost">
            {t.admin.certificates}
          </Link>
        }
      />
      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      {message && (
        <div className="dn-alert" style={{ background: "rgba(15,79,58,0.08)" }}>
          {message}
        </div>
      )}

      <form className="dn-card dn-form" onSubmit={onFilter} style={{ marginBottom: "1.25rem" }}>
        <div className="dn-form-grid">
          <label>
            {t.admin.certSearchHistory}
            <input value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <label>
            {t.admin.colStatus}
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{t.admin.certStatusAll}</option>
              <option value="generated">{t.admin.certStatusGenerated}</option>
              <option value="sent">{t.admin.certStatusSent}</option>
              <option value="failed">{t.admin.certStatusFailed}</option>
            </select>
          </label>
          <label>
            {t.admin.certLanguage}
            <select value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option value="">{t.admin.certLanguageAll}</option>
              <option value="en">{t.admin.certLanguageEn}</option>
              <option value="ar">{t.admin.certLanguageAr}</option>
              <option value="fr">{t.admin.certLanguageFr}</option>
            </select>
          </label>
          <label>
            {t.admin.certDateFrom}
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </label>
          <label>
            {t.admin.certDateTo}
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </label>
        </div>
        <div style={{ marginTop: "0.75rem" }}>
          <button type="submit" className="dn-btn dn-btn-gold" disabled={busy}>
            {t.admin.certFilter}
          </button>
        </div>
      </form>

      <div className="dn-table-wrap">
        <table className="dn-table dn-table-responsive">
          <thead>
            <tr>
              <th>{t.admin.certRecipient}</th>
              <th>{t.admin.certLanguage}</th>
              <th>{t.admin.certType}</th>
              <th>{t.admin.colStatus}</th>
              <th>{t.admin.certCreated}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <strong>{row.recipient_name}</strong>
                  <div className="dn-muted" style={{ fontSize: "0.85rem" }}>
                    {row.recipient_email}
                  </div>
                </td>
                <td>{row.language.toUpperCase()}</td>
                <td>{row.recipient_type}</td>
                <td>
                  <span className="dn-badge">{row.delivery_status}</span>
                  {row.delivery_error && (
                    <div className="dn-muted" style={{ fontSize: "0.8rem" }}>
                      {row.delivery_error}
                    </div>
                  )}
                </td>
                <td>{formatDateTime(row.created_at, locale)}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button
                    type="button"
                    className="dn-btn dn-btn-ghost"
                    disabled={busy || !row.pdf_file_key}
                    onClick={() => onDownload(row.id)}
                  >
                    {t.admin.certDownload}
                  </button>{" "}
                  <button
                    type="button"
                    className="dn-btn dn-btn-ghost"
                    disabled={busy || !row.pdf_file_key}
                    onClick={() => onResend(row.id)}
                  >
                    {t.admin.certResend}
                  </button>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={6} className="dn-muted">
                  {t.admin.certHistoryEmpty}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
